import axios from "axios";
import { ContentResponse, NeedleInput } from '../types';

interface AxiosError extends Error {
    response?: {
        status?: number;
    }
}

const BASE_DELAY = 60000;  // 1 minute
const MAX_DELAY = 300000;  // 5 minutes


const API_URL = process.env.REACT_APP_API_URL ?? '';
const API_USERNAME = process.env.REACT_APP_API_USERNAME ?? '';
const API_PASSWORD = process.env.REACT_APP_API_PASSWORD ?? '';

// Basic authentication header
const authHeader = {
    auth: {
        username: API_USERNAME,
        password: API_PASSWORD
    }
};

const exponentialBackoff = async (retries: number): Promise<void> => {
    const delay = Math.min(BASE_DELAY * (2 ** (6 - retries)), MAX_DELAY);
    await new Promise(resolve => setTimeout(resolve, delay));
}

export const generateFocusKeyphrase = async (title: string): Promise<string | undefined> => {
    const prompt = "Give me a 4 word focus keyphrase for yoast, for a page with this title " + title + ". Use British spelling. ONLY give me the 4 word keyphrase, don't give me any introduction or headings.";

    let retries = 6; // Number of retries
    while (retries > 0) {
        try {
            const response = await axios.post<{ content: string }>(
                `${API_URL}/openai/generate-content`,
                { prompt },
                authHeader
            );
            return response.data.content;
        } catch (error) {
            const axiosErr = error as AxiosError;
            if (axiosErr.response) {
                if (retries > 1) {
                    await exponentialBackoff(retries);
                    retries--;
                } else {
                    console.error("Exceeded maximum retries due to rate limit");
                    throw new Error("Rate limit exceeded. Please try again later.");
                }
            } else {
                if (retries > 1) {
                    retries--;
                } else {
                    console.error(error);
                    throw new Error("Error generating content from prompt");
                }
                
            }
        }
    }
};


export const generateContentFromPrompt = async (prompt: string): Promise<string | undefined> => {
    let retries = 6; // Number of retries
    while (retries > 0) {
        try {
            const response = await axios.post<{ content: string }>(
                `${API_URL}/openai/generate-content`,
                { prompt },
                authHeader
            );
            return response.data.content;
        } catch (error) {
            const axiosErr = error as AxiosError;
            if (axiosErr.response) {
                if (retries > 1) {
                    await exponentialBackoff(retries);
                    retries--;
                } else {
                    console.error("Exceeded maximum retries due to rate limit");
                    throw new Error("Rate limit exceeded. Please try again later.");
                }
            } else {
                if (retries > 1) {
                    retries--;
                } else {
                    console.error(error);
                    throw new Error("Error generating content from prompt");
                }
                
            }
        }
    }
};

export const fetchExistingContent = async (needle: string, postId: number | null | undefined): Promise<{ value: string } | undefined> => {
    try {
        const response = await axios.get<{ value: string }>(`${API_URL}/wordpress-posts/${postId}/beaver-data-value?needle=${needle}`, authHeader);
        return response.data;
    } catch (error) {
        console.error(error);
        throw new Error("Error fetching existing content");
    }
};

export const generateContent = async (
    needleInputs: NeedleInput[], 
    postId: number,
    title: string,
    generatedKeyPhrase: string | null,
    newWordCount: number,
    newParagraphCount: number | null,
    newSection: string
): Promise<ContentResponse | undefined> => {

    // Directly map needleInputs to form sections
    const sections = needleInputs.map(needle => {
        return {
            type: needle.type,
            wordCount: needle.wordCount,
            paragraphCount: needle.paragraphCount || null,
            content: needle.content
        };
    });

    // Step 2: Generate similar content
    let retries = 6; // Number of retries
    while (retries > 0) {
        try {
            const response = await axios.post<ContentResponse>(
                `${API_URL}/openai/generate-similar-content`,
                {
                    title: title,
                    keyPhrase: generatedKeyPhrase,
                    newWordCount: newWordCount,
                    newParagraphCount: newParagraphCount,
                    newSection: newSection,
                    sections: sections
                },
                authHeader
            );
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError;
            if (axiosErr.response) {
                if (retries > 1) {
                    await exponentialBackoff(retries);
                    retries--;
                } else {
                    console.error("Exceeded maximum retries due to rate limit");
                    throw new Error("Rate limit exceeded. Please try again later.");
                }
            } else {
                if (retries > 1) {
                    retries--;
                } else {
                    console.error(error);
                    throw new Error("Error generating content from prompt");
                }    
            }
        }
    }
};